// WhatsApp invitation utilities

export interface InvitationParams {
  name: string;
  phone: string;
  portalType: "owner" | "tenant" | "employee" | "provider";
  inviteLink: string;
}

export interface EntityInviteParams {
  name: string;
  entityType: "owner" | "tenant" | "employee" | "service_provider";
  inviteLink: string;
}

const portalMessages: Record<string, { ar: string; en: string }> = {
  owner: {
    ar: `من خلال البوابة يمكنك:
- متابعة عقاراتك ووحداتك
- الاطلاع على التقارير المالية
- مراجعة طلبات الصيانة
- الموافقة على الطلبات`,
    en: `Through the portal you can:
- Track your properties and units
- View financial reports
- Review maintenance requests
- Approve requests`,
  },
  tenant: {
    ar: `من خلال البوابة يمكنك:
- عرض تفاصيل عقدك
- متابعة الدفعات والفواتير
- تقديم طلبات الصيانة
- التواصل مع الإدارة`,
    en: `Through the portal you can:
- View your contract details
- Track payments and invoices
- Submit maintenance requests
- Contact management`,
  },
  employee: {
    ar: `من خلال البوابة يمكنك:
- متابعة المهام الموكلة إليك
- إدارة العقارات المسندة
- تحديث حالة الطلبات
- التواصل مع الفريق`,
    en: `Through the portal you can:
- Track assigned tasks
- Manage assigned properties
- Update request status
- Communicate with the team`,
  },
  provider: {
    ar: `من خلال البوابة يمكنك:
- استلام أوامر العمل
- تحديث حالة الطلبات
- إرسال الفواتير
- متابعة المدفوعات`,
    en: `Through the portal you can:
- Receive work orders
- Update request status
- Submit invoices
- Track payments`,
  },
  service_provider: {
    ar: `من خلال البوابة يمكنك:
- استلام أوامر العمل
- تحديث حالة الطلبات
- إرسال الفواتير
- متابعة المدفوعات`,
    en: `Through the portal you can:
- Receive work orders
- Update request status
- Submit invoices
- Track payments`,
  },
};

const portalTitles: Record<string, { ar: string; en: string }> = {
  owner: { ar: "بوابة المالك", en: "Owner Portal" },
  tenant: { ar: "بوابة المستأجر", en: "Tenant Portal" },
  employee: { ar: "بوابة الموظف", en: "Employee Portal" },
  provider: { ar: "بوابة مزود الخدمة", en: "Provider Portal" },
  service_provider: { ar: "بوابة مزود الخدمة", en: "Service Provider Portal" },
};

export function generateWhatsAppInviteMessage(
  params: InvitationParams,
  language: "ar" | "en" = "ar"
): string {
  const { name, portalType, inviteLink } = params;
  const portalTitle = portalTitles[portalType]?.[language] || portalTitles[portalType]?.ar;
  const features = portalMessages[portalType]?.[language] || portalMessages[portalType]?.ar;

  if (language === "ar") {
    return `مرحباً ${name}،

تم إنشاء حسابك في منصة دلالي لإدارة العقارات.

يمكنك الدخول إلى ${portalTitle} الخاصة بك من خلال الرابط التالي:
${inviteLink}

${features}

مع تحيات فريق دلالي`;
  }

  return `Hello ${name},

Your account has been created on Dallali Property Management Platform.

You can access your ${portalTitle} through the following link:
${inviteLink}

${features}

Best regards,
Dallali Team`;
}

/**
 * Generate a WhatsApp invite message for entity-based invitations
 * This is the primary function used by the unified invitation workflow
 */
export function generateEntityInviteMessage(
  params: EntityInviteParams,
  language: "ar" | "en" = "ar"
): string {
  const { name, entityType, inviteLink } = params;
  const portalTitle = portalTitles[entityType]?.[language] || portalTitles[entityType]?.ar;
  const features = portalMessages[entityType]?.[language] || portalMessages[entityType]?.ar;

  if (language === "ar") {
    return `مرحباً ${name}،

لقد تمت دعوتك للانضمام إلى ${portalTitle} في منصة دلّالي.

${features}

رابط التفعيل:
${inviteLink}

مع تحيات فريق دلّالي`;
  }

  return `Hello ${name},

You have been invited to join the ${portalTitle} on Dallali Platform.

${features}

Activation Link:
${inviteLink}

Best regards,
Dallali Team`;
}

export function openWhatsAppInvite(params: InvitationParams, language: "ar" | "en" = "ar"): void {
  const message = generateWhatsAppInviteMessage(params, language);
  const phone = params.phone.replace(/\D/g, "");
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
  window.open(whatsappUrl, "_blank");
}

export function generateInviteLink(baseUrl: string, token: string): string {
  return `${baseUrl}/invite/${token}`;
}

export function openWhatsApp(phone: string, message?: string): void {
  const cleanPhone = phone.replace(/\D/g, "");
  const url = message 
    ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/${cleanPhone}`;
  window.open(url, "_blank");
}

export function generateSchedulingMessage(schedulingLink: string, language: "ar" | "en" = "ar"): string {
  if (language === "ar") {
    return `مرحباً،

لتحديد موعد لمعاينة عقارك، يرجى استخدام الرابط التالي:
${schedulingLink}

مع تحيات فريق دلّالي`;
  }

  return `Hello,

To schedule your property inspection, please use the following link:
${schedulingLink}

Best regards,
Dallali Team`;
}
